import axios from 'axios';
import { fetchRawPageBlobUrl } from '../utils/mhtmlView';

// Настройка базового URL API
// В разработке используем прямой IP, в проде - относительный путь через прокси
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://92.63.106.142:4000/api/v2'
  : '/api/v2';

// Базовый URL для эндпоинтов создания задач (/api/generate, /api/generate_from_screenshot)
const TASK_API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://92.63.106.142:4000'  // Для локальной разработки
  : '';  // В production используем относительный путь

// Создаем экземпляр axios с базовой конфигурацией
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена к запросам
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок и обновления токена
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не повторный запрос, пытаемся обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const { accessToken: newAccessToken } = response.data;
        localStorage.setItem('accessToken', newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Если обновление токена не удалось, очищаем хранилище и перенаправляем на логин
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API для аутентификации
export const authAPI = {
  // Регистрация
  register: async (userData) => {
    const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);
    return response.data;
  },

  // Вход
  login: async (login, password) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      login,
      password,
    });
    return response.data;
  },

  // Подтверждение телефона
  verifyPhone: async (phone, code) => {
    const response = await axios.post(`${API_BASE_URL}/auth/verify-phone`, {
      phone,
      code,
    });
    return response.data;
  },

  // Повторная отправка кода
  resendVerification: async (phone) => {
    const response = await axios.post(`${API_BASE_URL}/auth/resend-verification`, {
      phone,
    });
    return response.data;
  },

  // Обновление токена
  refreshToken: async (refreshToken) => {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken,
    });
    return response.data;
  },

  // Выход
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  // Проверка валидности токена
  validateToken: async () => {
    const response = await apiClient.get('/auth/validate-token');
    return response.data;
  },
};

// API для пользователей
export const usersAPI = {
  // Получить профиль
  getProfile: async () => {
    const response = await apiClient.get('/users/profile');
    return response.data;
  },

  // Обновить профиль
  updateProfile: async (profileData) => {
    const response = await apiClient.put('/users/profile', profileData);
    return response.data;
  },

  // Загрузить аватар (multipart/form-data)
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await axios.post(
      `${API_BASE_URL}/users/avatar/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Загрузить аватар (base64)
  uploadAvatarBase64: async (base64Data) => {
    const response = await apiClient.put('/users/avatar', {
      avatar: base64Data,
    });
    return response.data;
  },

  // Получить историю задач пользователя
  getTasksHistory: async (params = {}) => {
    const response = await apiClient.get('/tasks', { params });
    return response.data;
  },

  // Обновить комментарий к задаче (автор или админ)
  updateTaskComment: async (taskId, comment) => {
    const response = await apiClient.patch(`/tasks/${taskId}/comment`, { comment });
    return response.data;
  },

  // ===== Сообщения (Messages) =====
  // Получить входящие сообщения
  getInboxMessages: async (params = {}) => {
    const response = await apiClient.get('/messages/inbox', { params });
    return response.data;
  },

  // Получить отправленные сообщения
  getSentMessages: async (params = {}) => {
    const response = await apiClient.get('/messages/sent', { params });
    return response.data;
  },

  // Получить сообщение по ID (автоматически помечает как прочитанное)
  getMessage: async (messageId) => {
    const response = await apiClient.get(`/messages/${messageId}`);
    
    // Проверяем, является ли message объектом (и не null), прежде чем возвращать его
    // Это предотвращает возврат строки, если response.data.message - это просто текст сообщения
    if (response.data.message && typeof response.data.message === 'object') {
        return response.data.message;
    }
    
    // Иначе возвращаем весь response.data (предполагая плоскую структуру)
    return response.data;
  },

  // Получить количество непрочитанных сообщений
  getUnreadCount: async () => {
    const response = await apiClient.get('/messages/unread-count');
    return response.data;
  },

  // Отправить сообщение
  sendMessage: async (messageData) => {
    const response = await apiClient.post('/messages/send', messageData);
    return response.data;
  },

  // Пометить сообщение как прочитанное (для ручного действия, если поддерживается)
  markMessageAsRead: async (messageId) => {
    // Согласно документации, GET /messages/:id помечает как прочитанное.
    // Если есть отдельный endpoint PUT, используем его, иначе оставляем старый или заглушку.
    // Возвращаем старый вариант на случай наличия скрытого endpoint'а
    const response = await apiClient.put(`/messages/${messageId}/read`);
    return response.data;
  },

  // Удалить сообщение
  deleteMessage: async (messageId) => {
    const response = await apiClient.delete(`/messages/${messageId}`);
    return response.data;
  },

  // ===== Уведомления (Notifications) =====
  notificationsAPI: {
    // Получить историю уведомлений
    getHistory: async (params = {}) => {
      const response = await apiClient.get('/notifications/history', { params });
      return response.data;
    },

    // Получить уведомление по ID
    getNotification: async (id) => {
      const response = await apiClient.get(`/notifications/${id}`);
      return response.data.notification ? response.data.notification : response.data;
    },

    // Отметить уведомление как прочитанное
    markAsRead: async (id) => {
      const response = await apiClient.put(`/notifications/${id}/read`);
      return response.data;
    }
  },
};

// API для администраторов
export const adminAPI = {
  // Получить список всех пользователей
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.isActive !== undefined && params.isActive !== '') {
      queryParams.append('isActive', params.isActive);
    }
    
    const queryString = queryParams.toString();
    const url = `/users/admin/users${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Получить упрощенный список пользователей для выбора
  getUsersList: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.isActive !== undefined && params.isActive !== '') {
      queryParams.append('isActive', params.isActive);
    }
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    const url = `/users/admin/users/list${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Получить детальную информацию о пользователе
  getUserDetails: async (userId) => {
    const response = await apiClient.get(`/users/admin/users/${userId}`);
    return response.data;
  },

  // Создать нового пользователя
  createUser: async (userData) => {
    const response = await apiClient.post('/users/admin/users', userData);
    return response.data;
  },

  // Изменить пароль пользователя
  updateUserPassword: async (userId, newPassword) => {
    const response = await apiClient.put(`/users/admin/users/${userId}/password`, {
      newPassword,
    });
    return response.data;
  },

  // Изменить статус и роль пользователя
  updateUserStatus: async (userId, statusData) => {
    const response = await apiClient.put(`/users/admin/users/${userId}/status`, statusData);
    return response.data;
  },

  // Изменить баланс пользователя
  updateUserBalance: async (userId, balanceData) => {
    const response = await apiClient.put(`/users/admin/users/${userId}/balance`, balanceData);
    return response.data;
  },

  // Удалить пользователя
  deleteUser: async (userId) => {
    const response = await apiClient.delete(`/users/admin/users/${userId}`);
    return response.data;
  },

  // Получить все задачи всех пользователей
  getAllTasks: async (params = {}) => {
    const response = await apiClient.get('/tasks/admin/all', { params });
    return response.data;
  },

  // Получить общую статистику по задачам
  getTasksStats: async () => {
    const response = await apiClient.get('/tasks/admin/stats');
    return response.data;
  },

  // Получить детали задачи по ID
  getTaskDetails: async (taskId) => {
    // Важно начинать путь со слэша, иначе axios склеит baseURL и получим /api/v2tasks/:id
    const response = await apiClient.get(`/tasks/${taskId}`);

    // Бэкенд может возвращать объект как { task: {...} }, так и просто { ... }.
    // Нормализуем, чтобы в UI всегда было тело задачи.
    const data = response.data;
    if (data?.task && typeof data.task === 'object') {
      return data.task;
    }
    return data;
  },

  // Получить статистику по авторам
  getAuthorsStats: async (params = {}) => {
    const response = await apiClient.get('/tasks/admin/authors-stats', { params });
    return response.data;
  },

  // Получить количество протоколов за период
  getProtocolsByPeriod: async (params = {}) => {
    const response = await apiClient.get('/tasks/admin/protocols-by-period', { params });
    return response.data;
  },

  // Получить список подключенных WebSocket клиентов
  getWebSocketClients: async () => {
    const response = await apiClient.get('/users/admin/websocket-clients');
    return response.data;
  },

  // ===== Мониторинг системы =====
  // Получить статистику ресурсов
  getSystemStats: async () => {
    const response = await apiClient.get('/admin/system/stats');
    return response.data;
  },

  // Получить историю нагрузки сервера
  getSystemLoadHistory: async (params = {}) => {
    const response = await apiClient.get('/admin/system/load-history', { params });
    return response.data;
  },

  // Получить активные задачи
  getActiveTasks: async () => {
    const response = await apiClient.get('/admin/system/active-tasks');
    return response.data;
  },

  // Получить историю ошибок
  getSystemErrors: async (params = {}) => {
    const response = await apiClient.get('/admin/system/errors', { params });
    return response.data;
  },

  // Отметить ошибку как решенную
  resolveSystemError: async (errorId) => {
    const response = await apiClient.put(`/admin/system/errors/${errorId}/resolve`);
    return response.data;
  },

  // ===== Управление балансами =====
  // Получить историю операций с балансом
  getBalanceHistory: async (params = {}) => {
    const response = await apiClient.get('/balance/history', { params });
    return response.data;
  },

  // Массовое пополнение балансов
  bulkTopupBalance: async (topupData) => {
    const response = await apiClient.post('/balance/bulk-topup', topupData);
    return response.data;
  },

  // Получить список тарифов
  getTariffs: async (params = {}) => {
    const response = await apiClient.get('/balance/tariffs', { params });
    return response.data;
  },

  // Создать тариф
  createTariff: async (tariffData) => {
    const response = await apiClient.post('/balance/tariffs', tariffData);
    return response.data;
  },

  // Обновить тариф
  updateTariff: async (tariffId, tariffData) => {
    const response = await apiClient.put(`/balance/tariffs/${tariffId}`, tariffData);
    return response.data;
  },

  // Удалить тариф
  deleteTariff: async (tariffId) => {
    const response = await apiClient.delete(`/balance/tariffs/${tariffId}`);
    return response.data;
  },

  // Получить пользователей с низким балансом
  getLowBalanceUsers: async (params = {}) => {
    const response = await apiClient.get('/balance/low-balance', { params });
    return response.data;
  },

  getSmsBoomAccount: async () => {
    const response = await apiClient.get('/balance/sms-boom');
    return response.data;
  },

  // Пересчёт зависших балансов (доначисление по оплаченным, но не учтённым платежам)
  creditMissingBalance: async () => {
    const response = await apiClient.post('/payments/credit-missing-balance', {});
    return response.data;
  },

  // ===== Уведомления =====
  // Отправить уведомление
  sendNotification: async (notificationData) => {
    const response = await apiClient.post('/notifications/send', notificationData);
    return response.data;
  },

  // Массовая рассылка
  bulkSendNotifications: async (bulkData) => {
    const response = await apiClient.post('/notifications/bulk', bulkData);
    return response.data;
  },

  // Получить список шаблонов
  getNotificationTemplates: async () => {
    const response = await apiClient.get('/notifications/templates');
    return response.data;
  },

  // Создать шаблон
  createNotificationTemplate: async (templateData) => {
    const response = await apiClient.post('/notifications/templates', templateData);
    return response.data;
  },

  // Обновить шаблон
  updateNotificationTemplate: async (templateId, templateData) => {
    const response = await apiClient.put(`/notifications/templates/${templateId}`, templateData);
    return response.data;
  },

  // Получить историю уведомлений
  getNotificationHistory: async (params = {}) => {
    const response = await apiClient.get('/notifications/history', { params });
    return response.data;
  },

  // Админ: Массовая отправка сообщений
  adminSendMessages: async (messageData) => {
    const response = await apiClient.post('/messages/admin/send', messageData);
    return response.data;
  },

  // Админ: Отправка сообщения всем пользователям
  adminSendToAll: async (messageData) => {
    const response = await apiClient.post('/messages/admin/send-all', messageData);
    return response.data;
  },

  // ===== Обучающие видео =====
  getTrainingVideos: async () => {
    const response = await apiClient.get('/training/admin');
    return response.data;
  },

  createTrainingVideo: async (videoData) => {
    const response = await apiClient.post('/training/admin', videoData);
    return response.data;
  },

  updateTrainingVideo: async (videoId, videoData) => {
    const response = await apiClient.put(`/training/admin/${videoId}`, videoData);
    return response.data;
  },

  deleteTrainingVideo: async (videoId) => {
    const response = await apiClient.delete(`/training/admin/${videoId}`);
    return response.data;
  },
};

// API для обучающих видео
export const trainingAPI = {
  getVideos: async () => {
    const response = await apiClient.get('/training');
    return response.data;
  },
};

// API для платежей
export const paymentsAPI = {
  // Получить список тарифов для покупки
  getTariffs: async () => {
    const response = await apiClient.get('/payments/tariffs');
    return response.data;
  },

  // Создать платеж
  createPayment: async (tariffId) => {
    const response = await apiClient.post('/payments/create', { tariffId });
    return response.data;
  },

  // Получить статус платежа
  getPaymentStatus: async (paymentId) => {
    const response = await apiClient.get(`/payments/${paymentId}/status`);
    return response.data;
  },

  // Получить детальную информацию о платеже
  getPayment: async (paymentId) => {
    const response = await apiClient.get(`/payments/${paymentId}`);
    return response.data;
  },

  // Получить историю платежей
  getHistory: async (params = {}) => {
    const response = await apiClient.get('/payments/history', { params });
    return response.data;
  },

  // Экспорт истории платежей в Excel (admin)
  exportPaymentHistory: async (params = {}) => {
    const response = await apiClient.get('/payments/history/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },
};

// API для счетов юрлиц
export const invoicesAPI = {
  getCompanyByInn: async (inn) => {
    const response = await apiClient.get('/invoices/company-by-inn', { params: { inn } });
    return response.data;
  },

  createInvoice: async (payload) => {
    const response = await apiClient.post('/invoices/create', payload, {
      responseType: 'blob',
    });
    return response;
  },

  downloadInvoice: async (invoiceId) => {
    const response = await apiClient.get(`/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  getAdminList: async (params = {}) => {
    const response = await apiClient.get('/invoices/admin/list', { params });
    return response.data;
  },

  getMyList: async (params = {}) => {
    const response = await apiClient.get('/invoices/my', { params });
    return response.data;
  },

  deleteInvoice: async (invoiceId) => {
    const response = await apiClient.delete(`/invoices/${invoiceId}`);
    return response.data;
  },

  creditInvoice: async (invoiceId) => {
    const response = await apiClient.post(`/invoices/${invoiceId}/credit`);
    return response.data;
  },
};

// API для заявок (Claims)
export const claimsAPI = {
  // Получить список заявок
  getClaims: async () => {
    const response = await apiClient.get('/claims');
    return response.data;
  },

  // Создать новую заявку
  createClaim: async (claimData) => {
    const response = await apiClient.post('/claims', claimData);
    return response.data;
  },

  // Получить заявку по ID
  getClaim: async (id) => {
    const response = await apiClient.get(`/claims/${id}`);
    return response.data;
  },

  // Обновить заявку
  updateClaim: async (id, claimData) => {
    const response = await apiClient.put(`/claims/${id}`, claimData);
    return response.data;
  },

  // Удалить заявку
  deleteClaim: async (id) => {
    const response = await apiClient.delete(`/claims/${id}`);
    return response.data;
  },
};

// API для работы с задачами через Node.js бэкенд
// ВСЕ запросы идут через бэкенд, без прямого обращения к Flask сервису
// Используем эндпоинты:
// - /api/generate и /api/generate_from_screenshot для создания задач
// - /api/v2/tasks для получения задач и списка задач (через apiClient)
export const flaskServiceAPI = {
  // Создать задачу (generate)
  generate: async (url, imageUrl, text) => {
    // Используем axios напрямую к эндпоинту /api/generate через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      throw new Error('Токен авторизации не найден. Пожалуйста, войдите в систему.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    };

    // Логирование для отладки (только в development)
    if (process.env.NODE_ENV === 'development') {
      console.log('Creating task with token:', accessToken ? `${accessToken.substring(0, 20)}...` : 'NO TOKEN');
      console.log('Request headers:', { ...headers, Authorization: 'Bearer ***' });
    }

    const payload = {
      url,
      search_type: imageUrl ? 'image' : 'text'
    };
    
    if (imageUrl) {
      payload.image_url = imageUrl;
    } else if (text) {
      payload.text = text;
    }

    try {
      const response = await axios.post(`${TASK_API_BASE}/api/generate`, payload, { headers });
      return response.data;
    } catch (error) {
      // Логирование ошибок для отладки
      if (process.env.NODE_ENV === 'development') {
        console.error('Task creation error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          url: '/api/generate',
          hasToken: !!accessToken
        });
      }
      
      // Обрабатываем ошибки авторизации
      if (error.response?.status === 401) {
        // Токен истек, пытаемся обновить через refresh token
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
              refreshToken,
            });
            const { accessToken: newAccessToken } = refreshResponse.data;
            localStorage.setItem('accessToken', newAccessToken);
            
            // Повторяем запрос с новым токеном
            headers.Authorization = `Bearer ${newAccessToken}`;
            const retryResponse = await axios.post(`${TASK_API_BASE}/api/generate`, payload, { headers });
            return retryResponse.data;
          }
        } catch (refreshError) {
          // Если обновление токена не удалось, очищаем и перенаправляем на логин
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/';
          throw new Error('Сессия истекла. Пожалуйста, войдите в систему снова.');
        }
      }
      // Для других ошибок просто пробрасываем их дальше
      throw error;
    }
  },

  // Создать задачу из скриншотов
  generateFromScreenshot: async (url, screenshots, callbackUrl) => {
    // Проверяем, что есть файлы
    if (!screenshots || screenshots.length === 0) {
      throw new Error('Необходимо загрузить хотя бы один скриншот');
    }

    const formData = new FormData();
    
    // Добавляем URL - это важно, так как сервер проверяет req.body.url
    formData.append('url', url);
    
    // Логирование для отладки
    if (process.env.NODE_ENV === 'development') {
      console.log('FormData URL:', url);
    }
    
    // Добавляем каждый файл с правильным именем поля
    // Важно: multer на сервере ожидает файлы в поле 'screenshots' (множественное число)
    // Используем точно такой же формат, как в старом клиенте: formData.append('screenshots', f.file)
    screenshots.forEach((file, index) => {
      if (!file) {
        console.warn(`File at index ${index} is null or undefined`);
        return;
      }
      // Проверяем, что это File или Blob объект
      if (file instanceof File || file instanceof Blob) {
        // Добавляем файл БЕЗ третьего параметра (имени файла), как в старом клиенте
        // Браузер автоматически возьмет имя из File объекта
        formData.append('screenshots', file);
      } else {
        console.error('Invalid file object:', file);
        throw new Error(`Файл ${index} имеет неверный формат`);
      }
    });
    
    if (callbackUrl) {
      formData.append('callback_url', callbackUrl);
    }

    // Используем axios напрямую к эндпоинту /api/generate_from_screenshot через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      throw new Error('Токен авторизации не найден. Пожалуйста, войдите в систему.');
    }
    
    const headers = {
      'Authorization': `Bearer ${accessToken}`
    };

    // Логирование для отладки (только в development)
    if (process.env.NODE_ENV === 'development') {
      console.log('Creating screenshot task:', {
        url,
        filesCount: screenshots.length,
        files: screenshots.map((f, i) => ({
          index: i,
          name: f?.name,
          size: f?.size,
          type: f?.type,
          isFile: f instanceof File,
          isBlob: f instanceof Blob
        })),
        hasToken: !!accessToken
      });
      console.log('Request headers:', { ...headers, Authorization: 'Bearer ***' });
    }

    // Используем fetch вместо axios для FormData, так как axios может неправильно обрабатывать файлы
    // Это соответствует тому, как это сделано в старом клиенте
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Sending request to:', `${TASK_API_BASE}/api/generate_from_screenshot`);
      }
      
      // Используем fetch для отправки FormData, как в старом клиенте
      const response = await fetch(`${TASK_API_BASE}/api/generate_from_screenshot`, {
        method: 'POST',
        headers: headers, // Не устанавливаем Content-Type - браузер сам установит с boundary
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const error = new Error(`Request failed with status code ${response.status}`);
        error.response = {
          status: response.status,
          statusText: response.statusText,
          data: data
        };
        throw error;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Request successful:', data);
      }
      
      return data;
    } catch (error) {
      // Логирование ошибок для отладки - ВСЕГДА логируем, не только в development
      const errorData = error.response?.data;
      console.error('Screenshot task creation error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: errorData,
        errorMessage: errorData?.error,
        errorCode: errorData?.code,
        message: error.message,
        requestUrl: `${TASK_API_BASE}/api/generate_from_screenshot`,
        requestData: {
          url,
          filesCount: screenshots.length,
          hasCallbackUrl: !!callbackUrl
        }
      });
      
      // Выводим полный ответ сервера для отладки
      console.error('Full server response:', JSON.stringify(errorData, null, 2));
      
      // Если это ошибка валидации, показываем более понятное сообщение
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.error) {
          throw new Error(errorData.error);
        }
        if (errorData?.code === 'MISSING_FILE') {
          throw new Error('Файлы скриншотов не были переданы на сервер');
        }
      }
      
      // Обрабатываем ошибки авторизации
      if (error.response?.status === 401) {
        // Токен истек, пытаемся обновить через refresh token
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
              refreshToken,
            });
            const { accessToken: newAccessToken } = refreshResponse.data;
            localStorage.setItem('accessToken', newAccessToken);
            
            // Повторяем запрос с новым токеном используя fetch
            headers.Authorization = `Bearer ${newAccessToken}`;
            const retryResponse = await fetch(`${TASK_API_BASE}/api/generate_from_screenshot`, {
              method: 'POST',
              headers: headers,
              body: formData
            });
            
            const retryData = await retryResponse.json();
            
            if (!retryResponse.ok) {
              const error = new Error(`Request failed with status code ${retryResponse.status}`);
              error.response = {
                status: retryResponse.status,
                statusText: retryResponse.statusText,
                data: retryData
              };
              throw error;
            }
            
            return retryData;
          }
        } catch (refreshError) {
          // Если обновление токена не удалось, очищаем и перенаправляем на логин
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/';
          throw new Error('Сессия истекла. Пожалуйста, войдите в систему снова.');
        }
      }
      // Для других ошибок просто пробрасываем их дальше
      throw error;
    }
  },

  // Получить статус задачи
  getTaskStatus: async (taskId) => {
    const response = await apiClient.get(`/tasks/${taskId}`);
    return response.data.task || response.data;
  },

  // Скачать результат
  downloadResult: async (taskId) => {
    // Используем axios напрямую к эндпоинту /api/download/:taskId через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    
    const response = await axios.get(`/api/download/${taskId}`, {
      headers,
      responseType: 'blob',
    });
    return response.data;
  },

  // Получить список скриншотов
  getScreenshots: async (taskId) => {
    // Используем axios напрямую через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    
    const response = await axios.get(`/api/screenshots/${taskId}`, { headers });
    return response.data;
  },

  // Получить скриншот
  getScreenshot: async (taskId, filename) => {
    // Используем axios напрямую через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    
    const response = await axios.get(`/api/screenshot/${taskId}/${filename}`, {
      headers,
      responseType: 'blob',
    });
    return response.data;
  },

  // Публичная ссылка на просмотр (обёртка view_mhtml → iframe raw_page)
  getMhtmlViewUrl: (taskId) => {
    return `/api/view_mhtml/${taskId}`;
  },

  getRawPageUrl: (taskId) => {
    return `/api/raw_page/${taskId}`;
  },

  // Скачать MHTML
  downloadMhtml: async (taskId) => {
    // Используем axios напрямую к эндпоинту /api/download_mhtml/:taskId через Node.js бэкенд
    const accessToken = localStorage.getItem('accessToken');
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    
    const response = await axios.get(`/api/download_mhtml/${encodeURIComponent(taskId)}`, {
      headers,
      responseType: 'blob',
    });
    return response.data;
  },

  // Загрузить конвертированную HTML-страницу (raw_page) для просмотра в iframe
  fetchRawPageViewUrl: async (taskId) => {
    const accessToken = localStorage.getItem('accessToken');
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    return fetchRawPageBlobUrl(taskId, { headers });
  },

  // Заменить MHTML у существующей задачи
  replaceMhtml: async (taskId, file) => {
    if (!file) {
      throw new Error('Необходимо выбрать MHTML-файл');
    }

    const buildFormData = () => {
      const formData = new FormData();
      formData.append('mhtml', file);
      return formData;
    };

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('Токен авторизации не найден. Пожалуйста, войдите в систему.');
    }

    const url = `${TASK_API_BASE}/api/tasks/${taskId}/mhtml`;

    const doRequest = async (token) => {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: buildFormData(),
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.error || `Request failed with status code ${response.status}`);
        error.response = { status: response.status, data };
        throw error;
      }
      return data;
    };

    try {
      return await doRequest(accessToken);
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
            refreshToken,
          });
          const { accessToken: newAccessToken } = refreshResponse.data;
          localStorage.setItem('accessToken', newAccessToken);
          return await doRequest(newAccessToken);
        }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        throw new Error('Сессия истекла. Пожалуйста, войдите в систему снова.');
      }
      throw error;
    }
  },
};

export default apiClient;

